import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  IconButton,
  Typography,
} from "@mui/material";
import { Add as AddIcon, Close as CloseIcon } from "@mui/icons-material";
import { LoadingButton } from "~/components/loading-button";
import { SelectMenu } from "~/components/Form/components/SelectMenu/SelectMenu";
import { Option } from "~/components/Form/components/SelectMenu/SelectMenu";
import { MembersRolesList } from "~/components/Members/MembersRolesList";
import {
  BASE_MEMBERS_ROLES,
  GROUPED_MEMBERS_ROLES,
  PERMISSIONS,
} from "~/components/Members/const";
import { hasAnyBaseRoleSelected } from "~/components/Members/utils/membersUtils";

type FormValues = {
  selectedRoles: string[];
};

export type AddSpaceRoleDialogProps = {
  open: boolean;
  spaceName: string;
  spaceMrn: string;
  onClose: () => void;
  onSave: (spaceMrn: string, roles: Array<{ mrn: string }>) => void;
  isLoading?: boolean;
};

export function AddSpaceRoleDialog({
  open,
  spaceName,
  spaceMrn,
  onClose,
  onSave,
  isLoading = false,
}: AddSpaceRoleDialogProps) {
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
    watch,
    setValue,
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      selectedRoles: [],
    },
    mode: "onChange",
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
  const [isRolesDropdownOpen, setIsRolesDropdownOpen] = useState(false);
  const watchSelectedRoles = watch("selectedRoles");

  const onSubmit = (data: FormValues) => {
    onSave(
      spaceMrn,
      data.selectedRoles.map((role) => ({ mrn: role })),
    );
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      data-testid="add-space-role-dialog"
      slotProps={{
        paper: {
          sx: {
            bgcolor: "background.default",
            position: "relative",
            overflow: "visible",
          },
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          pb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add Space Access
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {spaceName}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ overflow: "visible" }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Select the space roles for this user. Base roles define broad access
          levels, while detailed permissions provide granular control.
        </Typography>

        <Button
          variant="text"
          color="primary"
          onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
            setIsRolesDropdownOpen(true);
          }}
          startIcon={<AddIcon />}
          data-testid="add-space-role-add-roles-button"
          sx={{ mb: 2 }}
        >
          Add Roles
        </Button>

        <FormHelperText error>
          {errors.selectedRoles?.message || ""}
        </FormHelperText>

        <Controller
          name="selectedRoles"
          control={control}
          rules={{
            validate: (value) => {
              const isHasAnyBaseRoleSelected = hasAnyBaseRoleSelected(value);
              if (!isHasAnyBaseRoleSelected) {
                return "Must have at least one base role selected";
              }
              return true;
            },
          }}
          render={({ field, formState: { errors } }) => (
            <Box sx={{ position: "relative" }}>
              <SelectMenu
                isOpen={isRolesDropdownOpen}
                anchorRef={anchorEl}
                onClose={() => {
                  setAnchorEl(null);
                  setIsRolesDropdownOpen(false);
                }}
                maxHeight={208}
                options={GROUPED_MEMBERS_ROLES}
                onChange={(value) => {
                  field.onChange(value);
                }}
                multiple={true}
                value={field.value}
                variant="checkbox"
                error={!!errors.selectedRoles}
                helperText={errors.selectedRoles?.message || ""}
              />
            </Box>
          )}
        />

        <Box mt={3}>
          <MembersRolesList
            roles={watchSelectedRoles
              .map((roleMrn) => {
                const label = [...BASE_MEMBERS_ROLES, ...PERMISSIONS].find(
                  (role) => role.value === roleMrn,
                )?.label;

                return label ? { value: roleMrn, label } : null;
              })
              .filter((option): option is Option => option !== null)}
            onDelete={(roleMrn) => {
              setValue(
                "selectedRoles",
                watchSelectedRoles.filter((x) => x !== roleMrn),
                { shouldValidate: true, shouldDirty: true },
              );
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          justifyContent: "flex-end",
          px: 3,
          py: 2,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={isLoading}
          data-testid="add-space-role-cancel-button"
          sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}
        >
          Cancel
        </Button>
        <LoadingButton
          loading={isLoading}
          buttonText="Add"
          variant="contained"
          color="primary"
          onClick={handleSubmit(onSubmit)}
          disabled={isLoading || !isDirty || !isValid}
          data-testid="add-space-role-save-button"
          sx={{ textTransform: "uppercase", fontSize: "0.75rem" }}
        />
      </DialogActions>
    </Dialog>
  );
}
